import { useMemo, useState } from 'react';
import { chatAPI } from '../api/client';
import { captureEvent } from '../lib/posthog';
import type { PendingAction } from '../types';

interface ChatDrawerProps {
  isOpen: boolean;
  threadId: string;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

export function ChatDrawer({ isOpen, threadId, onClose, onUnreadChange }: ChatDrawerProps) {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const unreadCount = pendingActions.length;

  async function sendMessage() {
    const text = input.trim();
    if (!text || isStreaming) return;

    captureEvent('chat_message_sent', { message_length: text.length });
    const userMessage: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text };
    const assistantId = `a-${Date.now()}`;
    setMessages((prev) => [...prev, userMessage, { id: assistantId, role: 'assistant', text: '' }]);
    setInput('');
    setIsStreaming(true);

    try {
      for await (const event of chatAPI.stream({ thread_id: threadId, message: text })) {
        if (event.event === 'token') {
          const tokenText = String(event.data.text || '');
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantId ? { ...msg, text: msg.text + tokenText } : msg,
            ),
          );
        }

        if (event.event === 'pending_action') {
          const action: PendingAction = {
            id: String(event.data.id || ''),
            kind: event.data.kind as PendingAction['kind'],
            label: String(event.data.label || 'Pending action'),
            payload: (event.data.payload as Record<string, unknown>) || {},
          };
          setPendingActions((prev) => [action, ...prev]);
          onUnreadChange?.(pendingActions.length + 1);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Chat failed';
      setMessages((prev) =>
        prev.map((msg) => (msg.id === assistantId ? { ...msg, text: `Error: ${message}` } : msg)),
      );
    } finally {
      setIsStreaming(false);
    }
  }

  async function confirmAction(action: PendingAction) {
    try {
      await chatAPI.confirmAction(action.id, 'approved');
      setPendingActions((prev) => prev.filter((a) => a.id !== action.id));
      onUnreadChange?.(Math.max(0, unreadCount - 1));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to confirm action';
      setMessages((prev) => [...prev, { id: `err-${Date.now()}`, role: 'assistant', text: message }]);
    }
  }

  const drawerClass = useMemo(
    () =>
      `fixed inset-x-0 bottom-0 z-40 bg-white border-t border-edge rounded-t-2xl shadow-floating transition-transform duration-300 ${
        isOpen ? 'translate-y-0' : 'translate-y-full'
      }`,
    [isOpen],
  );

  return (
    <>
      {isOpen && <button className="fixed inset-0 z-30 bg-black/20" onClick={onClose} aria-label="Close chat" />}
      <section className={drawerClass} style={{ height: '70dvh' }}>
        <header className="px-4 py-3 border-b border-edge flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-ink">AI Copilot</p>
            <p className="text-xs text-ink-tertiary">Thread: {threadId}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-canvas" aria-label="Close">
            <span className="material-icons-round text-ink-secondary">close</span>
          </button>
        </header>

        <div className="h-[calc(70dvh-8.5rem)] overflow-y-auto px-4 py-3 space-y-3">
          {pendingActions.length > 0 && (
            <div className="space-y-2">
              {pendingActions.map((action) => (
                <div key={action.id} className="p-3 rounded-xl border border-warning/30 bg-warning-bg">
                  <p className="text-sm font-semibold text-warning">Action confirmation required</p>
                  <p className="text-sm text-ink mt-1">{action.label}</p>
                  <button
                    onClick={() => confirmAction(action)}
                    className="mt-2 px-3 py-1.5 text-xs rounded-lg bg-ink text-white hover:opacity-90"
                  >
                    Confirm action
                  </button>
                </div>
              ))}
            </div>
          )}

          {messages.length === 0 && (
            <p className="text-sm text-ink-tertiary">Ask: “Who should I follow up with this week?”</p>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                message.role === 'user'
                  ? 'ml-auto bg-ink text-white rounded-tr-sm'
                  : 'bg-canvas border border-edge text-ink rounded-tl-sm'
              }`}
            >
              {message.text}
            </div>
          ))}
        </div>

        <footer className="px-4 py-3 border-t border-edge flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void sendMessage();
            }}
            placeholder="Ask Clawer AI..."
            className="flex-1 px-3 py-2 rounded-lg border border-edge focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
          <button
            onClick={() => void sendMessage()}
            disabled={isStreaming || !input.trim()}
            className="px-4 py-2 rounded-lg bg-accent text-white disabled:opacity-50"
          >
            Send
          </button>
        </footer>
      </section>
    </>
  );
}

export default ChatDrawer;
